import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipTrigger, getUserFullName } from '@bliks-io/ui';
import { useOrgMembers } from '@/api/queries/useOrgMembers';
import { usePermissions } from '@/hooks/usePermissions';

export function OrgManagerHint({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const { data } = useOrgMembers({ status: ['active'] }, { enabled: can('User', 'read') });
  const managers = (data?.filter((member) => member.userTypes.includes('org_manager')) ?? []).sort(
    (a, b) =>
      getUserFullName(a.firstName, a.lastName).localeCompare(
        getUserFullName(b.firstName, b.lastName)
      )
  );

  // A paragraph is not focusable — tabIndex keeps the manager list keyboard-reachable.
  const hint = (
    <p className="text-xs text-muted-foreground" tabIndex={managers.length ? 0 : undefined}>
      {children}
    </p>
  );

  if (managers.length === 0) {
    return hint;
  }

  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>{hint}</TooltipTrigger>
      <TooltipContent>
        <span className="block font-medium">{t('userTypes.org_manager')}</span>
        {managers.map((manager) => (
          <span key={manager._id} className="block">
            {getUserFullName(manager.firstName, manager.lastName)} · {manager.email}
          </span>
        ))}
      </TooltipContent>
    </Tooltip>
  );
}
